import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Download, X } from "lucide-react";
import { isFleetDomain } from "../config/productConfig";

/**
 * InstallPWABanner — aparece quando o browser dispara beforeinstallprompt.
 * Em iOS (Safari) mostra instruções manuais ("Partilhar → Adicionar ao ecrã inicial").
 * Memoriza a dispensa para não voltar a aparecer na sessão.
 */

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios/i.test(navigator.userAgent) &&
  !window.matchMedia("(display-mode: standalone)").matches;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const InstallPWABanner = () => {
  const appName = isFleetDomain() ? "Aerofleet Center" : "ULM Training Centre";
  const [show, setShow] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    // Não mostrar se já estiver instalado como PWA
    if (isStandalone()) return;
    // Não mostrar se o utilizador já dispensou nesta sessão
    if (sessionStorage.getItem("pwa-banner-dismissed")) return;

    if (isIOS()) {
      // Em iOS não há evento beforeinstallprompt — mostrar instruções manuais
      setIsIOSDevice(true);
      setShow(true);
      return;
    }

    const handleInstallable = () => setShow(true);
    window.addEventListener("pwa-installable", handleInstallable);
    // Verificar se o evento já foi disparado antes deste componente montar
    if (window.__pwaInstallPrompt) setShow(true);

    return () => window.removeEventListener("pwa-installable", handleInstallable);
  }, []);

  const handleInstall = async () => {
    if (!window.__pwaInstallPrompt) return;
    window.__pwaInstallPrompt.prompt();
    const { outcome } = await window.__pwaInstallPrompt.userChoice;
    if (outcome === "accepted") {
      window.__pwaInstallPrompt = null;
    }
    setShow(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem("pwa-banner-dismissed", "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm
                 bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-xl
                 shadow-2xl shadow-black/50 p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-banner"
    >
      {/* Icon */}
      <img
        src={isFleetDomain() ? "/icons/fleet-icon-192.png" : "/icons/icon-192.png"}
        alt={appName}
        className="w-10 h-10 rounded-xl shrink-0 border border-slate-700"
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-cockpit-text-bright">
          Instalar {appName}
        </p>
        {isIOSDevice ? (
          <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
            Toque em{" "}
            <span className="inline-block text-slate-300 font-medium">
              Partilhar
            </span>{" "}
            e depois em{" "}
            <span className="inline-block text-slate-300 font-medium">
              Adicionar ao ecrã inicial
            </span>{" "}
            para instalar a app.
          </p>
        ) : (
          <p className="text-xs text-slate-400 mt-0.5">
            Adicione ao ecrã inicial para acesso rápido e uso offline.
          </p>
        )}

        {!isIOSDevice && (
          <Button
            size="sm"
            onClick={handleInstall}
            data-testid="pwa-install-btn"
            className="mt-2 h-7 text-xs bg-orange-500 hover:bg-orange-400 text-white border-0 px-3"
          >
            <Download className="w-3 h-3 mr-1.5" />
            Instalar
          </Button>
        )}
      </div>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        data-testid="pwa-dismiss-btn"
        className="shrink-0 p-1 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors"
        aria-label="Fechar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InstallPWABanner;
